/**
 * Agents — `docs/SCREENS.md` §5.
 *
 * The "No owner" tab must read 0, and it is the only tab whose count is worth anything, because it
 * is the one guaranteed by something other than this screen: a database CHECK plus the leaver
 * process, both tested. Every other number here is a filter.
 *
 * The count is rendered even when it is zero. A tab that appears only when something is wrong
 * teaches its readers that its absence means nothing was measured.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TIER_RULES } from "@hub/core/browser";
import { useApi } from "../lib/api";
import { Card, CountPill, KindBadge, Pill, TierBadge } from "../components";

interface Row {
  id: string;
  displayName: string;
  status: string;
  lifecycleState: string;
  owner: { id: string; displayName: string } | null;
  agentProfile: { capabilityTier: 1 | 2 | 3; owningTeam: string; purpose: string; dataScopes: string[] } | null;
}

type Tab = "all" | "tier3" | "review" | "orphan";

export function Agents() {
  const [tab, setTab] = useState<Tab>("all");
  const { data, loading, error } = useApi<{ identities: Row[] }>("/v1/identities?kind=agent&limit=500");
  const orphans = useApi<{ count: number }>("/v1/agents/orphans");

  const rows = data?.identities ?? [];

  const buckets = useMemo(() => ({
    all: rows,
    tier3: rows.filter((r) => r.agentProfile?.capabilityTier === 3),
    // "Needs review" is not a status field — it is a computed condition, so it cannot go stale.
    // An agent with no declared scopes is either brand new or was never finished, and both want a
    // person to look.
    review: rows.filter((r) =>
      r.agentProfile?.dataScopes.length === 0 || r.lifecycleState === "rescoped"),
    orphan: rows.filter((r) => r.owner === null),
  }), [rows]);

  const shown = buckets[tab];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Agents</h1>
          <p className="sub">
            Every non-human identity that can act, with the person accountable for it. An agent with
            no owner cannot be created — the database refuses it — so the count below is a fact and
            not a report.
          </p>
        </div>
        <Link className="btn primary" to="/onboard">Register agent</Link>
      </div>

      <div className="chips" style={{ marginBottom: 14 }}>
        <Tab k="all" tab={tab} set={setTab} label="All" n={buckets.all.length} />
        <Tab k="tier3" tab={tab} set={setTab} label="Tier 3" n={buckets.tier3.length} />
        <Tab k="review" tab={tab} set={setTab} label="Needs review" n={buckets.review.length} />
        <button className={`chip ${tab === "orphan" ? "on" : ""}`} onClick={() => setTab("orphan")}
                data-testid="tab-orphan">
          No owner <CountPill n={orphans.data?.count ?? buckets.orphan.length} />
        </button>
      </div>

      <Card>
        {error && <p className="err">{error.message}</p>}
        {loading && <p className="empty">Loading…</p>}
        {!loading && shown.length === 0 && (
          <p className="empty">
            {tab === "orphan"
              ? "No agent is without an accountable owner. That is the intended state, and it is held by a database constraint rather than by this screen."
              : "Nothing in this bucket."}
          </p>
        )}
        {shown.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Agent</th><th className="hide-m">Owner</th><th>Tier</th>
                  <th className="hide-m">Data scopes</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="clickable" data-testid="agent-row">
                    <td>
                      <Link to={`/agents/${r.id}`}>{r.displayName}</Link>
                      <div style={{ color: "var(--muted)", fontSize: 12 }}>
                        <KindBadge kind="agent" label={r.agentProfile?.owningTeam ?? "—"} />
                      </div>
                    </td>
                    <td className="hide-m">{r.owner?.displayName ?? <Pill tone="bad">none</Pill>}</td>
                    <td>
                      {r.agentProfile
                        ? <TierBadge tier={r.agentProfile.capabilityTier}
                                     meaning={TIER_RULES[r.agentProfile.capabilityTier].meaning} />
                        : <Pill tone="bad">no profile</Pill>}
                    </td>
                    <td className="hide-m">
                      {r.agentProfile?.dataScopes.length === 0
                        ? <Pill tone="warn">none declared</Pill>
                        : <span style={{ color: "var(--muted)" }}>{r.agentProfile?.dataScopes.length}</span>}
                    </td>
                    <td>
                      <Pill tone={
                        r.status === "retired" ? "bad" : r.lifecycleState === "active" ? "ok" : "warn"
                      }>
                        {r.status === "retired" ? "retired" : r.lifecycleState}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function Tab({ k, tab, set, label, n }: {
  k: Tab; tab: Tab; set: (t: Tab) => void; label: string; n: number;
}) {
  return (
    <button className={`chip ${tab === k ? "on" : ""}`} onClick={() => set(k)} data-testid={`tab-${k}`}>
      {label} <span style={{ color: "var(--dim)" }}>{n}</span>
    </button>
  );
}
